import { Phone, MessageCircle, Mail, MapPin, Instagram, Facebook, Youtube } from "lucide-react";
import { CONTACT, waLink, DEFAULT_WA_MESSAGE } from "@/config/siteConfig";
import { useSiteSettings, getPhones, getWaNumber, getEmail, getAddress, telHref } from "@/lib/siteSettings";
import { scrollToId } from "@/lib/scroll";
import { Logo } from "@/components/Logo";

const COMPANY = [
  { label: "About Us", href: "#about" },
  { label: "Properties", href: "#properties" },
  { label: "Projects", href: "#projects" },
  { label: "Contact", href: "#contact" },
];

const SERVICES = [
  { label: "Buy Property", href: "#buy" },
  { label: "Rent Property", href: "#rent" },
  { label: "Sell Property", href: "#sell" },
  { label: "Commercial Property", href: "#properties" },
  { label: "Plots", href: "#properties" },
];

export const Footer = () => {
  const { site } = useSiteSettings();
  const phones = getPhones(site);
  const email = getEmail(site);
  const address = getAddress(site);

  return (
    <footer id="contact" className="bg-navy text-white" data-testid="site-footer">
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">
          <div>
            <Logo dark />
            <p className="mt-5 text-sm text-white/55 leading-relaxed max-w-[260px]">
              Buy • Sell • Rent — premium property assistance with a customer-focused approach.
            </p>
            <div className="mt-6 flex gap-3">
              {[
                { icon: Instagram, href: CONTACT.socials.instagram, id: "instagram" },
                { icon: Facebook, href: CONTACT.socials.facebook, id: "facebook" },
                { icon: Youtube, href: CONTACT.socials.youtube, id: "youtube" },
              ].map((s) => (
                <a
                  key={s.id}
                  href={s.href}
                  aria-label={s.id}
                  data-testid={`footer-social-${s.id}`}
                  className="h-10 w-10 rounded-lg border border-white/15 flex items-center justify-center text-white/70 hover:text-gold hover:border-gold hover:-translate-y-0.5 transition-all duration-300"
                >
                  <s.icon size={17} />
                </a>
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-[12px] font-bold tracking-[0.22em] text-gold uppercase">Company</h4>
            <ul className="mt-5 space-y-3">
              {COMPANY.map((l) => (
                <li key={l.label}>
                  <button
                    onClick={() => scrollToId(l.href)}
                    data-testid={`footer-company-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="text-sm text-white/65 hover:text-gold transition-colors"
                  >
                    {l.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-[12px] font-bold tracking-[0.22em] text-gold uppercase">Services</h4>
            <ul className="mt-5 space-y-3">
              {SERVICES.map((l) => (
                <li key={l.label}>
                  <button
                    onClick={() => scrollToId(l.href)}
                    data-testid={`footer-services-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="text-sm text-white/65 hover:text-gold transition-colors"
                  >
                    {l.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-[12px] font-bold tracking-[0.22em] text-gold uppercase">Contact</h4>
            <ul className="mt-5 space-y-4 text-sm text-white/65">
              {phones.map((p) => (
                <li key={p.id}>
                  <a href={telHref(p.number)} className="flex items-center gap-3 hover:text-gold transition-colors" data-testid={`footer-phone-${p.id}`}>
                    <Phone size={15} className="text-gold shrink-0" /> {p.number}
                  </a>
                </li>
              ))}
              <li>
                <a href={waLink(DEFAULT_WA_MESSAGE, getWaNumber(site))} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 hover:text-gold transition-colors" data-testid="footer-whatsapp-link">
                  <MessageCircle size={15} className="text-gold shrink-0" /> WhatsApp Us
                </a>
              </li>
              <li>
                <a href={`mailto:${email}`} className="flex items-center gap-3 hover:text-gold transition-colors" data-testid="footer-email-link">
                  <Mail size={15} className="text-gold shrink-0" /> {email}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin size={15} className="text-gold shrink-0 mt-0.5" /> {address}
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-[13px] text-white/45">© 2026 RK Real Estate. All Rights Reserved.</p>
          <div className="flex gap-6">
            <button className="text-[13px] text-white/45 hover:text-gold transition-colors" data-testid="footer-privacy-link">Privacy Policy</button>
            <button className="text-[13px] text-white/45 hover:text-gold transition-colors" data-testid="footer-terms-link">Terms & Conditions</button>
            <a href="/admin/login" className="text-[13px] text-white/45 hover:text-gold transition-colors" data-testid="footer-admin-link">Admin</a>
          </div>
        </div>
      </div>
    </footer>
  );
};
